import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSecurityPosture, type TenantPosture } from '@/api/cipp';
import { StatCard } from '@/components/dashboard/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { CategoryBar, Donut } from '@/components/dashboard/charts';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { SERIES, SeriesLegend, SnapshotTrend, count, pct, toned } from '@/components/rmm/RmmDashboardKit';
import {
  CheckChip, DmarcChip, EnforcementChip, IssueChips, MfaCoverage, ScoreBar, hasSeverity,
} from '@/components/cipp/SecurityKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cippTenantPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * CIPP → Security posture. Every tenant's secure score, MFA coverage and enforcement,
 * conditional access, external forwarding, mail-domain auth (DMARC/SPF/DKIM) and
 * standards alignment — worst first. All from server-side background caches
 * (server/cipp/security.ts); nothing here calls CIPP on page load.
 */

type Filter = 'all' | 'critical' | 'mfa' | 'admins' | 'enforcement' | 'forwarding' | 'mail' | 'lowscore';

const FILTERS: { id: Filter; label: string; test: (p: TenantPosture) => boolean }[] = [
  { id: 'all',         label: 'All tenants',       test: () => true },
  { id: 'critical',    label: 'Critical',          test: (p) => hasSeverity(p, 'critical') },
  { id: 'enforcement', label: 'MFA not enforced',  test: (p) => !!p.mfa && p.mfa.unprotected > 0 },
  { id: 'mfa',         label: 'MFA unregistered',  test: (p) => !!p.mfa && p.mfa.notRegistered > 0 },
  { id: 'admins',      label: 'Admins without MFA', test: (p) => !!p.mfa && p.mfa.adminsNotRegistered > 0 },
  { id: 'forwarding',  label: 'External forwarding', test: (p) => !!p.forwarding?.smtp },
  { id: 'mail',        label: 'DMARC not enforced', test: (p) => p.mail?.dmarc === 'missing' || p.mail?.dmarc === 'none' },
  { id: 'lowscore',    label: 'Secure score < 50%', test: (p) => !!p.secureScore && p.secureScore.pct < 50 },
];

const SCORE_BANDS = [
  { id: '<40',    label: 'Under 40%', min: 0,  max: 40,  tone: 'destructive' as const },
  { id: '40-50',  label: '40–50%',    min: 40, max: 50,  tone: 'serious' as const },
  { id: '50-70',  label: '50–70%',    min: 50, max: 70,  tone: 'warning' as const },
  { id: '70+',    label: '70%+',      min: 70, max: 101, tone: 'success' as const },
];

const ENFORCEMENT_BUCKETS = [
  { id: 'Conditional access', tone: 'success' as const },
  { id: 'Security defaults',  tone: 'info' as const },
  { id: 'Per-user',           tone: 'neutral' as const },
  { id: 'Mixed',              tone: 'subtle' as const },
  { id: 'Partial',            tone: 'warning' as const },
  { id: 'None',               tone: 'destructive' as const },
];

const DMARC_BUCKETS = [
  { id: 'reject',     label: 'Reject',     tone: 'success' as const },
  { id: 'quarantine', label: 'Quarantine', tone: 'info' as const },
  { id: 'none',       label: 'p=none',     tone: 'warning' as const },
  { id: 'missing',    label: 'Missing',    tone: 'destructive' as const },
];

export default function CippSecurityDashboardPage() {
  const { data, isLoading, error, isFetching } = useSecurityPosture({ scoped: true });
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');

  const items = data?.items ?? [];

  const kpis = useMemo(() => {
    const scores = items.map((p) => p.secureScore?.pct).filter((n): n is number => n != null);
    const sum = (f: (p: TenantPosture) => number) => items.reduce((s, p) => s + f(p), 0);
    const known = sum((p) => (p.mfa ? p.mfa.registered + p.mfa.notRegistered : 0));
    const registered = sum((p) => p.mfa?.registered ?? 0);
    return {
      avgScore: scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null,
      scored: scores.length,
      mfaPct: known ? (registered / known) * 100 : null,
      notRegistered: sum((p) => p.mfa?.notRegistered ?? 0),
      unknownTenants: items.filter((p) => p.mfa && p.mfa.members > 0 && p.mfa.registered + p.mfa.notRegistered === 0).length,
      adminsNoMfa: sum((p) => p.mfa?.adminsNotRegistered ?? 0),
      unprotected: sum((p) => p.mfa?.unprotected ?? 0),
      unprotectedTenants: items.filter((p) => p.mfa && p.mfa.unprotected > 0).length,
      noneTenants: items.filter((p) => p.mfa?.enforcement === 'None').length,
      forwarding: sum((p) => p.forwarding?.smtp ?? 0),
      forwardingTenants: items.filter((p) => p.forwarding?.smtp).length,
      dmarcWeak: items.filter((p) => p.mail?.dmarc === 'missing' || p.mail?.dmarc === 'none').length,
      mailDomains: items.filter((p) => p.mail?.dmarc).length,
    };
  }, [items]);

  const charts = useMemo(() => ({
    scores: SCORE_BANDS.map((b) => ({
      id: b.id, label: b.label, tone: b.tone,
      value: items.filter((p) => p.secureScore && p.secureScore.pct >= b.min && p.secureScore.pct < b.max).length,
    })),
    enforcement: ENFORCEMENT_BUCKETS
      .map((b) => ({ id: b.id, label: b.id, tone: b.tone, value: items.filter((p) => p.mfa?.enforcement === b.id).length }))
      .filter((b) => b.value > 0),
    dmarc: DMARC_BUCKETS
      .map((b) => ({ ...b, value: items.filter((p) => p.mail?.dmarc === b.id).length }))
      .filter((b) => b.value > 0),
  }), [items]);

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.id, items.filter(f.test).length])) as Record<Filter, number>,
    [items],
  );
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const test = FILTERS.find((f) => f.id === filter)!.test;
    return items.filter(test).filter((p) => !s
      || p.tenant.displayName.toLowerCase().includes(s)
      || p.tenant.defaultDomainName.toLowerCase().includes(s)
      || (p.companyName ?? '').toLowerCase().includes(s));
  }, [items, filter, q]);

  return (
    <DesktopOnly title="CIPP dashboards are available on desktop" message="Open Cosmos on a larger screen to view the security dashboard.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-[650] tracking-[-0.02em] text-foreground">Security posture</h1>
            {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          {data?.fetchedAt && (
            <span className="text-xs text-muted-foreground">
              Gathered from CIPP {fmtRelative(data.fetchedAt)} (hourly)
              {' · '}mail domains from CIPP's daily Domain Analyser
            </span>
          )}
        </header>

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="py-16 text-sm text-destructive">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
        ) : data?.building ? (
          <Building done={data.progress.done} total={data.progress.total} />
        ) : data ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-6">
              <StatCard label="Avg secure score" value={kpis.avgScore != null ? pct(kpis.avgScore) : '—'} sub={`across ${kpis.scored} tenants`}
                tone={kpis.avgScore == null ? 'default' : kpis.avgScore >= 70 ? 'success' : kpis.avgScore >= 50 ? 'warning' : 'destructive'}
                onClick={() => setFilter('lowscore')} />
              <StatCard label="MFA registered" value={kpis.mfaPct != null ? pct(kpis.mfaPct) : '—'}
                sub={`${kpis.notRegistered} not registered${kpis.unknownTenants ? ` · ${kpis.unknownTenants} tenants unknown (no P1)` : ''}`}
                tone={kpis.mfaPct == null ? 'default' : kpis.mfaPct >= 95 ? 'success' : 'warning'} onClick={() => setFilter('mfa')} />
              <StatCard label="Outside MFA enforcement" value={kpis.unprotected}
                sub={`users in ${kpis.unprotectedTenants} tenants${kpis.noneTenants ? ` · ${kpis.noneTenants} with none` : ''}`}
                tone={kpis.noneTenants ? 'destructive' : kpis.unprotected ? 'warning' : 'success'} onClick={() => setFilter('enforcement')} />
              <StatCard label="Admins without MFA" value={kpis.adminsNoMfa} tone={kpis.adminsNoMfa ? 'destructive' : 'success'}
                onClick={() => setFilter('admins')} />
              <StatCard label="External forwarding" value={kpis.forwarding} sub={`mailboxes in ${kpis.forwardingTenants} tenants`}
                tone={kpis.forwarding ? 'warning' : 'success'} onClick={() => setFilter('forwarding')} />
              <StatCard label="DMARC not enforced" value={kpis.dmarcWeak} sub={`of ${kpis.mailDomains} mail domains`}
                tone={kpis.dmarcWeak ? 'warning' : 'success'} onClick={() => setFilter('mail')} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ChartCard title="Tenants by secure score">
                <CategoryBar data={toned(charts.scores)} valueFormatter={count} name="Tenants" height={220}
                  onSelect={(b) => { if (b.id === '<40' || b.id === '40-50') setFilter('lowscore'); }} />
              </ChartCard>
              <ChartCard title="How MFA is enforced">
                <Donut data={toned(charts.enforcement)} valueFormatter={count} height={220}
                  onSelect={(b) => { if (b.id === 'Partial' || b.id === 'None') setFilter('enforcement'); }} />
              </ChartCard>
              <ChartCard title="DMARC policy" action={<span className="text-xs text-muted-foreground">one mail domain per tenant</span>}>
                {data.mailBuilding
                  ? <div className="flex h-[220px] items-center justify-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking mail domains…</div>
                  : <Donut data={toned(charts.dmarc)} valueFormatter={count} height={220}
                      onSelect={(b) => { if (b.id === 'none' || b.id === 'missing') setFilter('mail'); }} />}
              </ChartCard>
            </div>

            <ChartCard className="mt-4" title="Trend · last 60 days"
              action={<SeriesLegend items={[{ label: 'Avg secure score', color: SERIES[0] }, { label: 'MFA registered', color: SERIES[1] }]} />}>
              <SnapshotTrend trend={data.trend} valueFormatter={pct} height={200} series={[
                { key: 'secureScorePct', label: 'Avg secure score', color: SERIES[0] },
                { key: 'mfaPct', label: 'MFA registered', color: SERIES[1] },
              ]} />
            </ChartCard>

            <section className="mt-4 rounded-lg border border-border bg-card p-5 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1 [scrollbar-width:thin]">
                  {FILTERS.map((f) => (
                    <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                      className={cn('shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors 2xl:px-3',
                        filter === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                      {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[f.id]}</span>
                    </button>
                  ))}
                </div>
                <SearchInput withIcon placeholder="Tenant, domain or client" className="w-64" value={q} onValueChange={setQ} />
              </div>
              <div className="overflow-x-auto rounded-md border">
                {/* Issues sits next to the tenant: it's what the worst-first sort is about, and on narrower
                    screens the table scrolls, so the detail columns are the ones that go off the edge. */}
                <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 1290 }}>
                  <colgroup>
                    <col style={{ width: 210 }} /><col /><col style={{ width: 130 }} /><col style={{ width: 125 }} /><col style={{ width: 125 }} />
                    <col style={{ width: 100 }} /><col style={{ width: 56 }} /><col style={{ width: 92 }} /><col style={{ width: 56 }} />
                    <col style={{ width: 60 }} /><col style={{ width: 96 }} />
                  </colgroup>
                  <thead className="whitespace-nowrap bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Tenant</th>
                      <th className="px-3 py-2 font-medium">Issues</th>
                      <th className="px-3 py-2 font-medium">Secure score</th>
                      <th className="px-3 py-2 font-medium">MFA registered</th>
                      <th className="px-3 py-2 font-medium">Enforced by</th>
                      <th className="px-3 py-2 font-medium" title="Conditional access policies: enabled · report-only">CA policies</th>
                      <th className="px-3 py-2 text-right font-medium" title="Mailboxes forwarding to an external address">Fwd</th>
                      <th className="px-3 py-2 font-medium">DMARC</th>
                      <th className="px-3 py-2 font-medium">SPF</th>
                      <th className="px-3 py-2 font-medium">DKIM</th>
                      <th className="px-3 py-2 font-medium" title="Lowest CIPP standards-template alignment">Standards</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">Nothing matches.</td></tr>}
                    {rows.map((p) => <PostureRow key={p.tenant.customerId} p={p} onOpen={() => navigate(cippTenantPath(p.tenant.customerId))} />)}
                  </tbody>
                </table>
              </div>
              {data.alignmentError && <p className="mt-2 text-[11px] text-warning">Standards alignment unavailable: {data.alignmentError}</p>}
              <p className="mt-2 text-[11px] text-muted-foreground">
                MFA counts cover enabled, licensed, non-guest users. Tenants without Entra ID P1 report no MFA registration data.
                Secure score is Microsoft's latest capture as stored by CIPP and can lag a few days.
              </p>
            </section>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function Building({ done, total }: { done: number; total: number }) {
  const p = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="mx-auto mt-16 max-w-md text-center">
      <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Gathering posture from CIPP
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {total ? `${done} of ${total} tenants` : 'Starting…'} — about a minute on first load, then served from cache.
      </p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function PostureRow({ p, onOpen }: { p: TenantPosture; onOpen: () => void }) {
  const mailTitle = p.mail
    ? [`${p.mail.domain}${p.mail.scorePct != null ? ` · CIPP domain score ${p.mail.scorePct}%` : ''}`, ...p.mail.notes].join('\n')
    : undefined;
  return (
    <tr className="cursor-pointer align-top hover:bg-muted/30" onClick={onOpen}>
      <td className="overflow-hidden px-3 py-2">
        <div className="truncate font-medium text-foreground" title={p.tenant.displayName}>{p.tenant.displayName}</div>
        <div className="truncate text-[11px] text-muted-foreground" title={p.companyName ?? p.tenant.defaultDomainName}>
          {p.companyName ?? <span className="italic">{p.tenant.defaultDomainName}</span>}
        </div>
      </td>
      <td className="px-3 py-2"><IssueChips issues={p.issues} /></td>
      <td className="px-3 py-2">
        <ScoreBar pct={p.secureScore?.pct}
          title={p.secureScore ? `${Math.round(p.secureScore.current)} of ${p.secureScore.max} points${p.secureScore.capturedAt ? ` · captured ${fmtRelative(p.secureScore.capturedAt)}` : ''}` : undefined} />
      </td>
      <td className="px-3 py-2"><MfaCoverage mfa={p.mfa} /></td>
      <td className="px-3 py-2"><EnforcementChip mfa={p.mfa} /></td>
      <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">
        {p.ca ? (p.ca.enabled + p.ca.reportOnly === 0 ? <span className="text-muted-foreground">None</span> : (
          <span title={p.ca.policies.map((x) => `${x.state === 'enabled' ? '●' : x.state === 'reportOnly' ? '◐' : '○'} ${x.name}`).join('\n')}>
            {p.ca.enabled}{p.ca.reportOnly ? <span className="text-muted-foreground"> · {p.ca.reportOnly} report</span> : null}
          </span>
        )) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">
        {p.forwarding?.smtp ? (
          <span className="font-medium text-warning" title={p.forwarding.mailboxes.filter((m) => m.external).map((m) => `${m.upn} → ${m.to}`).join('\n')}>{p.forwarding.smtp}</span>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2" title={mailTitle}><DmarcChip mail={p.mail} /></td>
      <td className="px-3 py-2" title={mailTitle}><CheckChip status={p.mail?.spf} /></td>
      <td className="px-3 py-2" title={mailTitle}><CheckChip status={p.mail?.dkim} /></td>
      <td className="px-3 py-2 text-xs tabular-nums">
        {p.alignment ? (
          <span className={p.alignment.score < 100 ? 'font-medium text-warning' : 'text-foreground'}
            title={p.alignment.templates.map((t) => `${t.name}: ${t.combined}%`).join('\n')}>{p.alignment.score}%</span>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
    </tr>
  );
}
